// AppShell export preparation and v3 job submission.
import { addData, updateData } from "./firestoreService";
import { showNotification } from "./notificationSystem";
import { createInitialDocumentStructure, optimizePatientHistory } from "./documentCreationUtils";
import { logAuditEventAsync, AuditActions } from "./auditLogger";
import { getSharedDocumentMetadata } from "./sharingService";
import { submitForProcessing } from "./processingService";

const MAX_DOCUMENTS_FOR_EXPORT = 16;
const prepareExportData = (patientDocuments = [], maxDocuments = MAX_DOCUMENTS_FOR_EXPORT) => {
  // Helper function to convert Firestore timestamp to Date
  const convertTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.seconds) {
      // Firestore timestamp format
      return new Date(timestamp.seconds * 1000);
    }
    if (typeof timestamp === 'string' || timestamp instanceof Date) {
      return new Date(timestamp);
    }
    return null;
  };

  // Use optimizePatientHistory to get clean patient data
  const optimizedHistory = optimizePatientHistory(patientDocuments);


  // Filter for completed documents with meaningful clinical data
  const validDocs = optimizedHistory
    .filter(doc => {
      const hasValidStatus = doc.status === 'completed';
      const hasValidTypeId = doc.typeId >= 1 && doc.typeId <= 4;

      return hasValidStatus && hasValidTypeId;
    })
    .slice(0, maxDocuments);

  if (validDocs.length === 0) {
    return {
      exportData: [],
      totalDocuments: 0,
      clinicalSummary: null,
      dateRange: null,
      combinedText: ""
    };
  }

  // Calculate date range from the documents
  const dates = validDocs
    .map(doc => convertTimestamp(doc.createdAt) || convertTimestamp(doc.modifiedAt))
    .filter(date => date)
    .sort((a, b) => a - b);

  const dateRange = dates.length > 0 ? {
    start: dates[0].toISOString(),
    end: dates[dates.length - 1].toISOString()
  } : null;

  // Define the clinical fields we want to extract
  const clinicalFields = [
    'chief_complaint',
    'psychiatric_history',
    'medical_history',
    'medications',
    'allergies',
    'family_history',
    'social_history',
    'substance_use',
    'trauma_history',
    'psychiatric_ROS',
    'mental_status_exam',
    'assessment_factors',
    'diagnostic_impressions_data',
    'risk_assessment',
    'treatment_plan',
    'after_visit_summary',
    'document_summary',
    'short_summary',
    'updated_assessment'
  ];

  // Combine text from all documents for processing
  const combinedText = validDocs
    .map((doc, index) => {
      const documentTexts = [];

      // Add document header with date
      const docDate = convertTimestamp(doc.createdAt) || convertTimestamp(doc.documentDate) || convertTimestamp(doc.modifiedAt);
      if (docDate) {
        documentTexts.push(`=== Document ${index + 1} (${docDate.toLocaleDateString()}) ===`);
      } else {
        documentTexts.push(`=== Document ${index + 1} ===`);
      }

      // Extract text from clinical fields
      clinicalFields.forEach(field => {
        if (doc[field]) {
          const value = doc[field];
          // Handle different value types
          if (typeof value === 'string' && value.trim()) {
            documentTexts.push(`${field.replace(/_/g, ' ').toUpperCase()}: ${value}`);
          } else if (typeof value === 'object' && value !== null && !value.seconds) {
            // Handle nested objects (but not timestamps)
            const text = value.text || value.content || value.value || JSON.stringify(value);
            if (text && text.trim()) {
              documentTexts.push(`${field.replace(/_/g, ' ').toUpperCase()}: ${text}`);
            }
          }
        }
      });

      // Also check for sections object if it exists
      if (doc.sections && typeof doc.sections === 'object') {
        Object.entries(doc.sections).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim()) {
            documentTexts.push(`${key.replace(/_/g, ' ').toUpperCase()}: ${value}`);
          } else if (typeof value === 'object' && value !== null && !value.seconds) {
            const text = value.text || value.content || value.value || '';
            if (text && text.trim()) {
              documentTexts.push(`${key.replace(/_/g, ' ').toUpperCase()}: ${text}`);
            }
          }
        });
      }

      return documentTexts.join('\n');
    })
    .filter(text => text && text.length > 20) // Filter out empty or too-short documents
    .join('\n\n---\n\n');

  return {
    exportData: validDocs,
    totalDocuments: validDocs.length,
    dateRange,
    combinedText
  };
};

export const handleExportProcessing = async ({
  selectedDocumentType,
  selectedDocumentTypeId,
  dropdownState,
  patientId,
  navigate,
  setButtonText,
  setCurrentUploadDocId,
  setIsProcessing,
  userSettings,
  setIsFunctionHeaderOpen,
  documentData,
  setCurrentExportDocId,
  shareInfo = null,
  userId = null,
  addPendingJob = () => {},
  removePendingJob = () => {},
}) => {
  let newDocId = null;

  try {
    // Use the functions (now they should always be defined due to fallbacks)
    setIsProcessing(true);
    setButtonText("Generating Export...");

    if (documentData.totalDocuments === 0) {
      showNotification("No clinical data available for export", "error", 4000);
      setIsProcessing(false);
      setButtonText("Begin Export");
      return false;
    }

    // Validate required data
    if (!dropdownState?.patientData) {
      console.error("Patient data is missing from dropdownState");
      showNotification("Patient data is not available. Please ensure a patient is selected.", "error", 4000);
      setIsProcessing(false);
      setButtonText("Begin Export");
      return false;
    }

    // Get the sections for this document type
    const documentType = dropdownState.documentTypes?.find((type) => type.id === selectedDocumentTypeId);
    const documentSections = documentType?.sections || null;

    // Step 1: Create initial document structure
    const startTime = Date.now();
    const initialData = {
      ...createInitialDocumentStructure(
        "export",
        selectedDocumentType,
        selectedDocumentTypeId,
        documentType?.category || 3,
        startTime,
        documentSections
      ),
      ...getSharedDocumentMetadata(userId, shareInfo)
    };

    // Add export-specific metadata
    initialData.isExport = true;
    initialData.sourceDocumentCount = documentData.totalDocuments;
    initialData.sourceDocumentIds = documentData.exportData.map(d => d.id);
    initialData.dateRange = documentData.dateRange;

    // Step 2: Create document in Firestore
    try {
      newDocId = await addData(`patients/${patientId}/documents`, initialData);
      setCurrentUploadDocId(newDocId);
      setCurrentExportDocId(newDocId);
      addPendingJob(newDocId, selectedDocumentType || "Export", patientId,
        `${dropdownState.patientData.firstName || ""} ${dropdownState.patientData.lastName || ""}`.trim());

      // Log document creation for stats
      logAuditEventAsync(AuditActions.DOCUMENT_CREATE, {
        resourceId: newDocId,
        patientId: patientId,
        metadata: {
          typeId: selectedDocumentTypeId,
          typeName: selectedDocumentType,
          analyticsCategory: documentType?.subcategory || null,
          source: 'export',
        }
      });
    } catch (error) {
      console.error("Error creating initial record for export:", error);
      if (newDocId) removePendingJob(newDocId);
      setButtonText("Begin Export");
      setCurrentUploadDocId(null);
      setIsProcessing(false);
      return false;
    }

    // Step 3: Queue v3 processing. The active-job listener owns progress and completion.
    const result = await submitForProcessing({
      text: documentData.combinedText,
      documentTypeId: documentType?.firestoreId || selectedDocumentTypeId,
      patientData: dropdownState.patientData,
      patientDocuments: [],
      documentId: newDocId,
      patientId,
      source: "export",
    });

    if (!result.success) {
      removePendingJob(newDocId);
      if (result.status !== "prerequisites_not_met") {
        showNotification(result.error || "Export generation failed", "error", 6000);
        await updateData(`patients/${patientId}/documents`, newDocId, {
          status: "error",
          modifiedAt: new Date(),
        });
      }
      setButtonText("Begin Export");
      setIsProcessing(false);
      return false;
    }

    console.log("[Export] Processing initiated successfully");
    showNotification(`${selectedDocumentType} generation started. Select this message to open it.`, "success", 10000, {
      key: `export-started-${newDocId}`,
      priority: true,
      onClick: () => navigate("/document", { state: { patientId, docId: newDocId } }),
    });
    setButtonText("Begin Export");
    setIsProcessing(false);
    return true;

  } catch (error) {
    console.error("[Export] Error processing export:", error);
    if (newDocId) removePendingJob(newDocId);
    setIsProcessing(false);
    setButtonText("Begin Export");
    showNotification(`Export processing failed: ${error.message}`, "error", 6000);

    // Clean up document if it was created
    if (newDocId && patientId) {
      try {
        await updateData(`patients/${patientId}/documents`, newDocId, {
          status: "error",
          modifiedAt: new Date()
        });
      } catch (updateError) {
        console.error("Error updating document status:", updateError);
      }
    }

    return false;
  }
};

export { prepareExportData };
